import { useEffect, useRef, useState } from 'react'
import { CheckCircle, Send } from 'lucide-react'

const TYPES = ['General Feedback', 'Bug Report', 'Feature Request', 'Data Correction', 'Privacy Concern']
const EMPTY = { message: '', type: 'General Feedback', pageUrl: '', name: '', email: '', followUp: false, includeBuild: false }
const controlStyle = 'w-full rounded-lg border border-gray-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-civitas-navy disabled:bg-gray-100'
const labelStyle = 'block text-sm font-medium text-civitas-navy mb-1'

function payloadFor(form, marker) {
  if (!form.message.trim()) return { field: 'message', error: 'Please enter a message.' }
  if (form.message.length > 4000) return { field: 'message', error: 'Keep your message to 4000 characters or fewer.' }
  if (form.name.length > 100) return { field: 'name', error: 'Keep your name to 100 characters or fewer.' }
  if (form.email.length > 254) return { field: 'email', error: 'Keep your email to 254 characters or fewer.' }
  const email = form.email.trim()
  if (form.followUp && !email) return { field: 'email', error: 'Enter an email if you would like follow-up.' }
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return { field: 'email', error: 'Enter a valid email, or leave it blank without requesting follow-up.' }
  let pageUrl = ''
  if (form.pageUrl.trim()) {
    try {
      const url = new URL(form.pageUrl.trim())
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || form.pageUrl.length > 2048) throw Error('invalid URL')
      pageUrl = url.origin + url.pathname
    } catch { return { field: 'pageUrl', error: 'Enter an http or https URL of 2048 characters or fewer, without a username or password, or leave it blank.' } }
  }
  return { payload: { message: form.message.trim(), type: form.type, name: form.name.trim(), email, followUp: form.followUp, pageUrl, buildMarker: form.includeBuild ? marker : '' } }
}

export default function Feedback() {
  const [form, setForm] = useState({ ...EMPTY })
  const [receipt, setReceipt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const inFlight = useRef(false)
  const attempt = useRef(null)
  const controller = useRef(null)
  const formRef = useRef(null)
  const statusRef = useRef(null)
  const errorRef = useRef(null)
  const marker = import.meta.env.VITE_BUILD_MARKER || 'development'

  useEffect(() => () => controller.current?.abort(), [])
  useEffect(() => { if (receipt) statusRef.current?.focus() }, [receipt])
  useEffect(() => {
    if (error?.field) formRef.current?.elements.namedItem(error.field)?.focus()
    else if (error) errorRef.current?.focus()
  }, [error])

  function handleChange(event) {
    const { name, value, type, checked } = event.target
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (inFlight.current) return
    const validated = payloadFor(form, marker)
    if (validated.error) { setError(validated); return }
    const body = JSON.stringify(validated.payload)
    inFlight.current = true
    setLoading(true)
    setError(null)
    controller.current = new AbortController()
    const timeout = setTimeout(() => controller.current?.abort(), 15000)
    try {
      // An unchanged retry retains its token even if the first commit succeeded
      // but its acknowledgement was lost. Contact details never enter storage.
      if (attempt.current?.body !== body) attempt.current = { body, key: crypto.randomUUID() }
      const response = await fetch('/api/feedback', {
        method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', redirect: 'error',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': attempt.current.key },
        body, signal: controller.current.signal,
      })
      if (!response.ok) throw Error('unconfirmed')
      const acknowledgement = await response.json()
      if (acknowledgement?.accepted !== true || acknowledgement.reference !== attempt.current.key) throw Error('unconfirmed')
      setReceipt(acknowledgement.reference)
    } catch {
      setError({ error: 'We could not confirm that your feedback was saved. Your draft is still here. Please try again; retrying the same draft will not create another report.' })
    } finally {
      clearTimeout(timeout)
      inFlight.current = false
      setLoading(false)
    }
  }

  const errorProps = name => error?.field === name ? { 'aria-invalid': true, 'aria-describedby': 'feedback-error' } : {}
  if (receipt) return (
    <div className="section max-w-xl text-center">
      <div role="status" tabIndex={-1} ref={statusRef} className="rounded-lg focus:outline focus:outline-2 focus:outline-civitas-navy">
        <CheckCircle aria-hidden="true" className="h-16 w-16 text-green-700 mx-auto mb-4" />
        <h1 className="font-display text-2xl text-civitas-navy mb-3">Your feedback has been saved</h1>
        <p className="mb-2">Thank you for sharing your thoughts with the BECivitas product team.</p>
        <p className="mb-6">Report reference: <span className="break-all font-mono text-sm">{receipt}</span></p>
      </div>
      <button className="btn-secondary" onClick={() => { setReceipt(''); setForm({ ...EMPTY }); attempt.current = null }}>Send more feedback</button>
    </div>
  )

  return (
    <div className="section max-w-xl">
      <h1 className="font-display text-3xl text-civitas-navy mb-2">Feedback &amp; Bug Reports</h1>
      <p className="text-gray-700 mb-6">Share a problem, a data correction, an idea, or any other feedback. Only your message is required. Your report is private to the BECivitas product team.</p>
      <form ref={formRef} onSubmit={handleSubmit} noValidate className="card flex flex-col gap-5">
        <div>
          <label htmlFor="feedback-message" className={labelStyle}>Message (required)</label>
          <p id="feedback-hint" className="text-sm text-gray-700 mb-2">
            {form.type === 'Data Correction' ? 'If useful, describe the affected information and a supporting source. What would you change?' :
              form.type === 'Bug Report' ? 'If useful, tell us what you were trying to do, what happened, and what you expected.' :
                'What would you like us to know? Positive or critical feedback is welcome. Examples of what happened can help.'}
          </p>
          <textarea id="feedback-message" name="message" value={form.message} onChange={handleChange} rows={6} maxLength={4000} required disabled={loading}
            aria-describedby={'feedback-hint feedback-count' + (error?.field === 'message' ? ' feedback-error' : '')} aria-invalid={error?.field === 'message' || undefined} className={controlStyle} />
          <p id="feedback-count" className="text-sm text-gray-700">{form.message.length}/4000 characters. Please do not include passwords or sensitive personal information.</p>
        </div>
        <div>
          <label htmlFor="feedback-type" className={labelStyle}>Category (optional)</label>
          <select id="feedback-type" name="type" value={form.type} onChange={handleChange} disabled={loading} className={controlStyle}>
            {TYPES.map(type => <option key={type}>{type}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="feedback-page" className={labelStyle}>Page or source URL (optional)</label>
          <input id="feedback-page" name="pageUrl" type="url" value={form.pageUrl} onChange={handleChange} maxLength={2048} disabled={loading} {...errorProps('pageUrl')} className={controlStyle} />
          <p className="text-sm text-gray-700 mt-1">Query strings and fragments are removed before sending. You can leave this blank or clear it.</p>
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" name="includeBuild" checked={form.includeBuild} onChange={handleChange} disabled={loading} className="mt-1" />
          <span className="break-all">Include this app build for context: {marker} (optional)</span>
        </label>
        <div>
          <label htmlFor="feedback-name" className={labelStyle}>Name (optional)</label>
          <input id="feedback-name" name="name" value={form.name} onChange={handleChange} maxLength={100} autoComplete="off" disabled={loading} {...errorProps('name')} className={controlStyle} />
        </div>
        <div>
          <label htmlFor="feedback-email" className={labelStyle}>Email (optional)</label>
          <input id="feedback-email" name="email" type="email" value={form.email} onChange={handleChange} maxLength={254} autoComplete="off" disabled={loading} {...errorProps('email')} className={controlStyle} />
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" name="followUp" checked={form.followUp} onChange={handleChange} disabled={loading} className="mt-1" />
          <span>May we contact you about this feedback? (optional; requires email)</span>
        </label>
        {error && <p id="feedback-error" role="alert" tabIndex={-1} ref={errorRef} className="text-red-800 text-sm">{error.error}</p>}
        <button type="submit" disabled={loading} className="btn-primary flex items-center justify-center gap-2 disabled:opacity-60">
          <Send aria-hidden="true" className="h-4 w-4" />{loading ? 'Sending…' : 'Send feedback'}
        </button>
      </form>
    </div>
  )
}
